import { useEffect, useLayoutEffect, useState } from 'react'

import { UseFormGetValues, UseFormReset, UseFormSetValue } from 'react-hook-form'

import authCookieManager from '@App/cookies/auth_cookie_manager'
import { useAppDispatch, useAppSelector } from '@App/hooks'
import { Wallet } from '@App/interfaces/CommonInterfaces'
import { makeSpotOrderRequest } from '@App/redux/slices/sockets/FrontServiceConnectionSlice'
import { clearEditTrade } from '@App/redux/slices/trade/trades/TradesSlice'
import {StopLoss, TakeProfit, Trade} from '@App/redux/slices/trade/trades/types'

import { currentUser } from '@Redux/slices/user/selectors'

import { calcPnL } from '../../../../../OrderHistory/tables/OrderHistoryTable/utils/calcFunctions'
import {FormType, SportLossValues, TakeProfitValues} from '../../model'

export const TABS = [
  { name: 'Edit', value: 1 },
  { name: 'Close', value: 2 },
]

export const useEditTrade = ({
  order,
  wallet,
  orderType,
  getValues,
  setValue,
  reset,
  tickerDataPrice,
  onResetForm,
  getStopLossValues,
  getTakeProfitValues,
}: {
  order: Trade
  wallet: Wallet | null
  orderType: string
  getValues: UseFormGetValues<FormType>
  setValue: UseFormSetValue<FormType>
  reset: UseFormReset<FormType>
  tickerDataPrice: number
  onResetForm?: () => void
  getStopLossValues: (rate: number, quantity: number, stopLoss?: StopLoss) => SportLossValues
  getTakeProfitValues: (rate: number, quantity: number, takeProfit?: TakeProfit) => TakeProfitValues
}) => {
  const user = useAppSelector(currentUser)

  const dispatch = useAppDispatch()
  const onSubmit = (data: FormType) => {
    if (wallet) {
      dispatch(
        makeSpotOrderRequest({
          ...data,
          take_profit_trigger_type: data.take_profit_trigger_type ?? undefined,
          stop_loss_trigger_type: data.stop_loss_trigger_type,
          cancellation_date: data.cancellation_date?.toISOString() ?? null,
          order_type: orderType as any,
          token: authCookieManager.getAccessToken() || '',
          user_id: user.user_id,
          amount: data.amount,
          market_id: order.market.id,
          action: order.action_type,
          rate: tickerDataPrice,
          wallet_id: wallet.id as any as number,
          type: 'change',
          trade_id: order.id,
        }),
      )
    }

    dispatch(clearEditTrade())
  }

  const resetForm = () => {
    const values = getValues()
    reset({
      ...values,
      wallet_value: wallet?.amount ?? 0,
      amount: order.quantity,
      price_in_wallet_currency: order.amount,
      ...getStopLossValues(order.open_rate, order.quantity, order?.stop_loss),
      ...getTakeProfitValues(order.open_rate, order.quantity, order?.take_profit)
    })
    onResetForm?.()
  }

  useLayoutEffect(() => {
    resetForm()
  }, [order])

  useEffect(() => {
    if (!wallet) return

    setValue('wallet_value', wallet.amount, { shouldDirty: false })
  }, [wallet?.amount])

  const pnl = Number(calcPnL(order.quantity, order.open_rate, tickerDataPrice, order.action_type))
  const [activeTab, setActiveTab] = useState(TABS[0].value)

  return { onSubmit, activeTab, setActiveTab, pnl, resetForm }
}
